import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import Typography from "@mui/material/Typography";
import { SxProps, Theme } from "@mui/material";

interface BarcodeProps {
  value: string;
  sx?: SxProps<Theme>;
  displayValue?: boolean;
  width?: number;
  height?: number;
}

/**
 * Barcode component renders a barcode using JsBarcode and displays it inside a MUI Typography component.
 * @param {BarcodeProps} props - The props for the Barcode component.
 * @param {string} props.value - The value to encode in the barcode.
 * @param {SxProps<Theme>} [props.sx] - Optional MUI sx prop for custom styling.
 * @param {boolean} [props.displayValue] - Whether to display the value below the barcode.
 * @param {number} [props.width] - Maximum width in pixels for the barcode container.
 * @param {number} [props.height] - Height in pixels for the barcode.
 *
 * The barcode is rendered as an SVG element and updates when the value changes.
 */
function Barcode({ value, sx, displayValue = false, width, height = 44 }: BarcodeProps) {
  const barcodeRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (barcodeRef.current) {
      JsBarcode(barcodeRef.current, value, {
        format: "CODE128", // Common barcode format, supports your number
        displayValue, // Show the number below the barcode
        width: 1.5, // Reduced width for better fit
        height,
        fontSize: 14,
        textMargin: 3,
        margin: 2,
      });
    }
  }, [value, height, displayValue]);

  return (
    <Typography sx={{ maxWidth: width ? `${width}px` : "100%", overflow: "hidden", display: "flex", alignItems: "center", ...sx }}>
      <svg ref={barcodeRef} style={{ maxWidth: "100%", height: "auto" }} />
    </Typography>
  );
}

export default Barcode;
